<template>
  <v-container v-if="currentUser" fluid>
    <v-container>
      <h1 class="container-title">{{ $t("userTitle") }}</h1>
    </v-container>
    <v-container>
      <v-row>
        <v-col cols="12" sm="4">
          <v-card max-width="375" class="mx-auto">
            <v-img
              v-if="currentUser.avatar"
              :src="currentUser.avatar"
              :aspect-ratio="16 / 10"
            ></v-img>
            <v-img
              v-else
              src="@/assets/images/user.png"
              style="top: 20px; margin-bottom: 20px"
            ></v-img>
            <v-form>
              <v-card-title class="pb-2">
                <v-chip outlined color="success" label>
                  <v-icon left>mdi-check-circle-outline</v-icon>
                  {{ $t("userVerified") }}
                </v-chip>
                <v-spacer></v-spacer>
                <v-dialog v-model="dialogEdit" persistent max-width="550px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon>
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <v-card tile>
                    <v-card-title>
                      <span class="headline">{{ $t("userEdit") }}</span>
                    </v-card-title>
                    <v-card-text>
                      <v-text-field
                        :label="$t('userFormName')"
                        :placeholder="$t('userFormName')"
                        required
                        @input="$v.currentUser.name.$touch()"
                        @blur="$v.currentUser.name.$touch()"
                        v-model="currentUser.name"
                        :error-messages="nameErrors"
                        name="name"
                        >{{ currentUser.name }}</v-text-field
                      >
                      <v-text-field
                        :label="$t('userFormPhone')"
                        :placeholder="$t('userFormPhone')"
                        required
                        @input="$v.currentUser.phone.$touch()"
                        @blur="$v.currentUser.phone.$touch()"
                        v-model="currentUser.phone"
                        :error-messages="phoneErrors"
                        name="phone"
                        >{{ currentUser.phone }}</v-text-field
                      >
                      <v-text-field
                        label="E-mail"
                        placeholder="E-mail"
                        required
                        @input="$v.currentUser.email.$touch()"
                        @blur="$v.currentUser.email.$touch()"
                        v-model="currentUser.email"
                        :error-messages="emailErrors"
                        name="email"
                        >{{ currentUser.email }}</v-text-field
                      >
                      <v-file-input
                        ref="avatar"
                        :label="$t('userFormAvatar')"
                        :placeholder="$t('userAvatarUpload')"
                        clearable
                        append-clear-icon
                        accept="image/*"
                        prepend-icon=""
                        name="photo"
                        v-model="file"
                      ></v-file-input>
                      <small>* {{ $t("validation") }}</small>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="green darken-1"
                        text
                        @click="edit()"
                        :disabled="$v.$invalid"
                        >{{ $t("btnSave") }}</v-btn
                      >
                      <v-btn color="red darken-1" text @click="cancelEdit()">{{
                        $t("btnCancel")
                      }}</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <v-btn icon @click="logOut()">
                  <v-icon>mdi-exit-run</v-icon>
                </v-btn>
              </v-card-title>
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon class="mr-3">
                    <v-icon>mdi-account-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      currentUser.name
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="mr-3">
                    <v-icon>mdi-phone</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      currentUser.phone
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="mr-3">
                    <v-icon>mdi-email</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      currentUser.email
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-form>
          </v-card>
        </v-col>
        <v-col cols="12" sm="8">
          <locations-table />
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import LocationsTable from "@/components/LocationsTable.vue";
import { validationMixin } from "vuelidate";
import { required, email } from "vuelidate/lib/validators";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "User",

  mixins: [validationMixin],

  validations: {
    currentUser: {
      email: { required, email },
      name: { required },
      phone: { required },
    },
  },

  components: {
    LocationsTable,
  },

  data() {
    return {
      dialogEdit: false,
      file: null,
      user: null,
    };
  },

  computed: {
    phoneErrors() {
      const errors = [];
      if (!this.$v.currentUser.phone.$dirty) return errors;
      !this.$v.currentUser.phone.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.currentUser.name.$dirty) return errors;
      !this.$v.currentUser.name.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.currentUser.email.$dirty) return errors;
      !this.$v.currentUser.email.email &&
        errors.push(this.$i18n.t("validationEmail"));
      !this.$v.currentUser.email.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    ...mapGetters(["currentUser"]),
  },

  methods: {
    ...mapActions(["loadUser", "logOut", "updateUser"]),
    edit() {
      this.user = this.currentUser;
      let formData = new FormData();
      formData.append("name", this.user.name);
      formData.append("phone", this.user.phone);
      formData.append("email", this.user.email);
      if (this.file) {
        formData.append("avatar", this.file, this.file.name);
      }
      this.updateUser({ id: this.user.id, formData: formData });
      this.dialogEdit = false;
    },
    cancelEdit() {
      this.loadUser();
      this.dialogEdit = false;
    },
  },

  watch: {
    dialogEdit(visible) {
      if (!visible) {
        this.$v.$reset();
      }
    },
  },
};
</script>
